import React, { useState } from "react";
import { Constants } from "@eagerdog/constants";
import { AddOnDto } from "@eagerdog/interfaces";
import { httpService } from "src/services/http.service";
import { Config } from "@eagerdog/config";
import { toast } from "src/components/Toast/ToastManager";
import Button from "src/components/Button/Button";
import Input from "src/components/Input/Input";
import DatePick from "src/components/DatePick/DatePick";
import ImageUploader from "src/components/ImageUploader/ImageUploader";
import styles from "./CreateStoreItem.module.scss";

interface IProps {
  clubId: string;
  onFinish: () => void;
  onCancel: () => void;
}

const CreateStoreItem: React.FC<IProps> = ({ clubId, onFinish, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [expiryDate, setExpiryDate] = useState<undefined | Date>(undefined);
  const [fee, setFee] = useState("");
  const [imageUrl, setImageUrl] = useState<string>("");

  const uploadImage = async (file: File): Promise<string> => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await httpService.post(
        `${Config.web.REACT_APP_API_URL}/api/storage/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data.value;
    } catch (error) {
      console.error('Failed to upload image:', error);
      throw error;
    }
  };

  const handleImageChange = async (file: File | null) => {
    if (file) {
      try {
        const url = await uploadImage(file);
        setImageUrl(url);
        
        toast.show({
          title: "Image Uploaded",
          content: "Store item image uploaded successfully",
          duration: 3000,
          type: "success"
        });
      } catch (e: any) {
        toast.show({
          title: "Upload Failed",
          content: "Failed to upload store item image",
          duration: 5000,
          errorDetails: e,
          type: "fail"
        });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title || !description || !fee) {
      toast.show({
        title: "Validation Error",
        content: "Title, description, and price are required.",
        type: "fail",
        duration: 5000
      });
      return;
    }

    setLoading(true);

    const newAddOn: AddOnDto = {
      scope: Constants.add_on_scope.club,
      title,
      description,
      fee: parseFloat(fee),
      is_active: true,
      expires_at: expiryDate ? expiryDate.toISOString() : null as any,
      image_url: imageUrl || undefined,
    };

    try {
      await httpService.post(
        `${Config.web.REACT_APP_API_URL}/api/clubs/${clubId}/store/items`,
        newAddOn
      );
      toast.show({
        title: "Success",
        content: "Store item created successfully!",
        type: "success",
        duration: 5000
      });
      onFinish();
    } catch (error: any) {
      console.error("Error creating store item:", error);
      toast.show({
        title: "Error",
        content: error.response?.data?.message || "Failed to create store item",
        type: "fail",
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.createStoreItem}>
      <div className={styles.title}>Create Store Item</div>
      <div className={styles.formWrap}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>Item Image</label>
            <ImageUploader
              label=""
              currentImageUrl={imageUrl}
              aspectRatio={1}
              circularCrop={false}
              onImageChange={handleImageChange}
              maxWidth={600}
              maxHeight={600}
            />
            <small className={styles.helpText}>
              Upload a square image for this store item. Recommended size: 600x600px.
            </small>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="title">Title *</label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Club T-Shirt"
              disabled={loading}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              className={styles.textarea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the item..."
              disabled={loading}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="fee">Price *</label>
            <Input
              id="fee"
              type="number"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
              placeholder="e.g., 25.00"
              disabled={loading}
              step="0.01"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="description">Expiry Date</label>
            <DatePick
              label=""
              value={expiryDate}
              isClearable={true}
              showTimeSelect={true}
              onChange={(date: Date | null) => {
                if (date) {
                  const utcDate = new Date(Date.UTC(
                    date.getUTCFullYear(),
                    date.getUTCMonth(),
                    date.getUTCDate(),
                    date.getUTCHours(),
                    date.getUTCMinutes(),
                    0, 0
                  ));
                  setExpiryDate(utcDate);
                } else {
                  setExpiryDate(undefined);
                }
              }}
            />
          </div>

          <div className={styles.actions}>
            <Button type="button" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Creating..." : "Create Item"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateStoreItem;

